import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import io from "socket.io-client";
import '../styles/OperatorPanel.css';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const socket = io(BASE_URL);

export default function OperatorPanel({ counterId }) {
  const [currentToken, setCurrentToken] = useState(null); // Token being actively served by this operator
  const [waitingTokens, setWaitingTokens] = useState([]); // List of ALL waiting tokens
  const [skippedTokens, setSkippedTokens] = useState([]); // List of ALL skipped tokens
  const [msg, setMsg] = useState(""); // For feedback messages to the operator
  const [counterNumber, setCounterNumber] = useState(""); // Add this line
  const navigate = useNavigate(); // <<< NEW: Get navigate hook >>>

  // Fetch counters and set counter number
  useEffect(() => {
    axios.get(`${BASE_URL}/api/counters`)
      .then(res => {
        const match = res.data.find(c => String(c.id) === String(counterId));
        setCounterNumber(match ? match.number : counterId);
      })
      .catch(err => {
        setCounterNumber(counterId);
        console.error("Error fetching counters:", err);
      });
  }, [counterId]);

  // --- Logout Function for OperatorPanel ---
  const handleLogout = () => {
    localStorage.removeItem('currentUser'); // Clear user from local storage
    navigate('/login'); // Redirect to login page
    // App.js will also update its state on next render/refresh
  };

  // --- Function to fetch all data relevant to this operator ---
  const fetchAllOperatorData = useCallback(() => {
    // 1. Fetch token currently assigned to THIS counter
    axios.get(`${BASE_URL}/api/tokens/active-called`)
      .then(res => {
        const mine = res.data.find(t => t.counter_id === counterId);
        setCurrentToken(mine || null);
      })
      .catch(err => console.error("Error fetching current token:", err));

    // 2. Fetch ALL waiting tokens
    axios.get(`${BASE_URL}/api/tokens/waiting`)
      .then(res => setWaitingTokens(res.data))
      .catch(err => console.error("Error fetching waiting tokens:", err));

    // 3. Fetch ALL skipped tokens
    axios.get(`${BASE_URL}/api/tokens/skipped`)
      .then(res => setSkippedTokens(res.data))
      .catch(err => console.error("Error fetching skipped tokens:", err));
  }, [counterId]);

  // --- useEffect to load data and set up real-time listeners ---
  useEffect(() => {
    fetchAllOperatorData(); // Initial data load when component mounts or counterId changes

    // Listen for any token updates that might affect the queue or current token
    socket.on('update_token', fetchAllOperatorData); // Fired when token status changes
    socket.on('new_token', fetchAllOperatorData);     // Fired when a new token is created

    // Cleanup listeners when component unmounts
    return () => {
      socket.off('update_token', fetchAllOperatorData);
      socket.off('new_token', fetchAllOperatorData);
    };
  }, [counterId, fetchAllOperatorData]); // Dependency array: re-run if counterId prop or fetchAllOperatorData changes

  // --- Operator Actions ---

  // Assign next token to this counter
  function callNextToken() {
    axios.post(`${BASE_URL}/api/tokens/assign-next`, { counter_id: counterId })
      .then(res => {
        setCurrentToken(res.data); // Update local state for currently served token
        setMsg(`New token assigned: ${res.data.token_number}`);
      })
      .catch(err => {
        setMsg("No more waiting tokens!"); // Display error if no tokens available
        console.error("Error calling next token:", err);
      });
  }

  // Mark currently served token as 'served'
  function markAsServed() {
    if (!currentToken) return; // Only if there's a token to serve
    axios.patch(`${BASE_URL}/api/tokens/${currentToken.id}`, {
      status: "served",
      counter_id: counterId // Ensure counter_id is sent so it is saved
    }).then(res => {
      setCurrentToken(null); // Clear currently served token
      setMsg("Token marked as served.");
    })
    .catch(err => console.error("Error marking as served:", err));
  }

  // Send currently served token back to 'waiting' queue
  function sendToWaitingQueue() {
    if (!currentToken) return; // Only if there's a token to send back
    axios.patch(`${BASE_URL}/api/tokens/${currentToken.id}`, {
      status: "waiting",
      counter_id: null // Clear counter assignment
    }).then(res => {
      setCurrentToken(null); // Clear currently served token
      setMsg("Token sent back to waiting queue.");
    })
    .catch(err => console.error("Error sending to waiting:", err));
  }

  // --- NEW: Mark currently served token as 'skipped' ---
  function skipToken() { // Renamed from previous 'skipToken' to be clear about action
    if (!currentToken) return;
    axios.patch(`${BASE_URL}/api/tokens/${currentToken.id}`, {
      status: "skipped", // Set status to 'skipped'
      counter_id: null   // Clear counter assignment
    }).then(res => {
      setCurrentToken(null);
      setMsg("Token marked as skipped.");
    })
    .catch(err => console.error("Error skipping token:", err));
  }

  // Re-announce the currently served token (sends Socket.IO event to Display)
  function reannounceToken() {
    if (currentToken && currentToken.id) {
      console.log("OperatorPanel: Emitting 'reannounce_token' for token ID:", currentToken.id, "at counter:", currentToken.counter_number);
      socket.emit('reannounce_token', 
        { token_id: currentToken.id,
          counter_id: currentToken.counter_id }); // Use currentToken.counter_id
      setMsg("Re-announcement triggered!");
    } else {
      setMsg("No token to re-announce.");
      console.warn("OperatorPanel: No current token to re-announce.");
    }
  }

  // --- Recall a Skipped Token (from skippedTokens list) ---
  function recallSkippedToken(tokenId) {
    axios.patch(`${BASE_URL}/api/tokens/${tokenId}/recall`) // Calls the backend's recall endpoint
      .then(res => {
        setMsg(`Token ${tokenId} recalled to waiting queue.`);
        fetchAllOperatorData(); // Refresh all lists
      })
      .catch(err => console.error("Error recalling token:", err));
  }

  return (
    <div className="operator-page-wrapper">
    <div className="operator-main-header"></div>
    <div className="operator-panel-container">
      {/* <<< NEW: Logout Button >>> */}
      <button 
        onClick={handleLogout} 
        className="operator-btn btn-danger" 
      >
        Logout
      </button>
      {/* <<< END NEW: Logout Button >>> */}
      
      <h2 className="operator-panel-title">Operator Panel (Counter {counterNumber})</h2>
      <p className="operator-panel-msg">{msg}</p>

      {/* Section for Calling and Serving Tokens */}
      <div className="operator-section current-service-section">
        <h3 className="operator-section-title">Current Service</h3>
        {currentToken ? (
          <div>
            <p className="serving-token-label">Serving Token: <span className="serving-token-number">{currentToken.token_number}</span></p>
            <div className="operator-action-buttons">
              <button onClick={markAsServed} className="operator-btn btn-served">Mark as Served</button>
              <button onClick={sendToWaitingQueue} className="operator-btn btn-waiting">Send to Waiting</button>
              <button onClick={skipToken} className="operator-btn btn-skip">Skip Token</button>
              <button onClick={reannounceToken} className="operator-btn btn-reannounce">Re-announce Token</button>
            </div>
          </div>
        ) :
          <div className="operator-no-token">
            <p>No token currently assigned.</p>
            <button onClick={callNextToken} className="operator-btn btn-next">Call Next Token</button>
          </div>
        }
      </div>

      {/* Section for Waiting Queue */}
      <div className="operator-section waiting-queue-section">
        <h3 className="operator-section-title">Waiting Queue ({waitingTokens.length} tokens)</h3>
        {waitingTokens.length === 0 ? (
          <p className="operator-empty">No one is waiting.</p>
        ) : (
          <ul className="operator-list waiting-list">
            {waitingTokens.map(token => (
              <li key={token.id} className="operator-list-item waiting-list-item">
                <span className="operator-token-number">{token.token_number}</span>
                <span className="operator-token-time">
                  ({new Date(token.created_at).toLocaleTimeString()})
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* NEW Section for Skipped Tokens */}
      <div className="operator-section skipped-tokens-section">
        <h3 className="operator-section-title">Skipped Tokens ({skippedTokens.length} tokens)</h3>
        {skippedTokens.length === 0 ? (
          <p className="operator-empty">No tokens have been skipped.</p>
        ) : (
          <ul className="operator-list skipped-list">
            {skippedTokens.map(token => (
              <li key={token.id} className="operator-list-item skipped-list-item">
                <span className="operator-token-number">{token.token_number}</span>
                <button onClick={() => recallSkippedToken(token.id)} className="operator-btn btn-recall">Recall</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
    </div>
  );
}